/**
 * Slug sanitizer - frontend mirror of `backend/functions/src/shared/slugSanitizer.ts`.
 *
 * Keep both files in sync: the user types into the input, this function
 * canonicalizes the value in real time, and the canonical form is what we
 * send to `checkSlugAvailability` / `reserveSlug`. The server applies the
 * same sanitization defensively, then enforces `ONBOARDING_SLUG_REGEX`.
 *
 * Steps:
 *   1. NFKD decomposition.
 *   2. Strip combining diacritics (U+0300..U+036F): e-acute -> e, a-grave -> a.
 *   3. Lowercase.
 *   4. Map space, dot, underscore to `-`.
 *   5. Drop anything outside `[a-z0-9-]`.
 *   6. Collapse hyphen runs and trim edges.
 */

const COMBINING_DIACRITICS = /[̀-ͯ]/g;
const SEPARATORS = /[\s._]+/g;
const NON_ALLOWED = /[^a-z0-9-]/g;
const HYPHEN_RUN = /-+/g;
const EDGE_HYPHENS = /^-+|-+$/g;

export function sanitizeOnboardingSlug(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .normalize('NFKD')
    .replace(COMBINING_DIACRITICS, '')
    .toLowerCase()
    .replace(SEPARATORS, '-')
    .replace(NON_ALLOWED, '')
    .replace(HYPHEN_RUN, '-')
    .replace(EDGE_HYPHENS, '');
}

/**
 * Per-keystroke sanitizer for the onboarding input field. Preserves
 * leading/trailing hyphens and hyphen runs so the user can type a
 * hyphenated handle character by character without the input fighting
 * them. The canonical form is what gets sent to the server.
 */
export function sanitizeOnboardingSlugLive(
  raw: string | null | undefined,
): string {
  if (!raw) return '';
  return raw
    .normalize('NFKD')
    .replace(COMBINING_DIACRITICS, '')
    .toLowerCase()
    .replace(SEPARATORS, '-')
    .replace(NON_ALLOWED, '');
}

const RESERVED_ARCHIVE_PREFIX = 'archive-';

export function isReservedOnboardingSlug(
  sanitized: string,
  reservedNames: readonly string[],
): boolean {
  if (!sanitized) return false;
  if (sanitized.startsWith(RESERVED_ARCHIVE_PREFIX)) return true;
  return reservedNames.includes(sanitized);
}
